import { DeleteOutlined, ExclamationCircleOutlined } from "@ant-design/icons";
import type { HttpError } from "@refinedev/core";
import { useDelete } from "@refinedev/core";
import { Button, Space, Spin, Typography } from "antd";
import { useEffect, useMemo, useState } from "react";
import { useModal } from "utils";

import { EAZY_COLORS } from "../constants";
import type { BuildingItem, EazySyncInfo } from "./BuildingSearchResults";

const { Text } = Typography;

interface DeleteRegistrationPanelProps {
  selectedTarget: BuildingItem | null;
  onSelectedTargetChange: (target: BuildingItem | null) => void;
  searchResults?: BuildingItem[];
  onSearchResultsChange?: (results: BuildingItem[]) => void;
  onRefresh?: () => void;
}

export const DeleteRegistrationPanel = ({
  selectedTarget,
  onSelectedTargetChange,
  searchResults,
  onSearchResultsChange,
  onRefresh,
}: DeleteRegistrationPanelProps) => {
  const [isDeleting, setIsDeleting] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const { mutateAsync: deleteAsync } = useDelete();
  const { modal, contextHolder } = useModal();

  const isLoggedInStatus = (status?: EazySyncInfo["status"]) => {
    return status === "CREATED" || status === "UPDATED" || status === "UPDATE_FAILED";
  };

  const isDeletedStatus = (status?: EazySyncInfo["status"]) => {
    return status === "DELETED";
  };

  const getStatusText = (status?: EazySyncInfo["status"]): string => {
    if (!status) return "-";

    if (status === "CREATED" || status === "UPDATED" || status === "UPDATE_FAILED") {
      return "登録済";
    }

    if (status === "DELETED") {
      return "削除済";
    }

    return "-";
  };

  const convertDeviceContract = (deviceContract: string): string => {
    if (deviceContract === "dearisController") {
      return "dearis";
    }
    if (deviceContract === "PabbitLite") {
      return "PP03A";
    }
    return deviceContract;
  };

  const handleDeleteClick = () => {
    modal.confirm({
      centered: true,
      icon: <ExclamationCircleOutlined className="text-[#FAAD14]" />,
      title: "削除操作の確認",
      content: (
        <div>
          <Text>この対象の登録を削除してもよろしいですか？</Text>
          <br />
          <Text>この操作は元に戻すことができません。</Text>
        </div>
      ),
      cancelText: "キャンセル",
      okText: "削除",
      okButtonProps: { danger: true },
      onOk: handleDeleteConfirm,
    });
  };

  const handleDeleteConfirm = () => {
    if (!selectedTarget) return;

    const propertyId = selectedTarget.property.id;
    const buildingId = selectedTarget.building_id;

    setIsDeleting(true);
    setErrorMessage(null);

    deleteAsync({
      resource: "properties",
      id: `${propertyId}/buildings/${buildingId}/eazy_registry`,
    })
      .then(() => {
        const updatedTarget: BuildingItem = {
          ...selectedTarget,
          eazy_sync: {
            ...selectedTarget.eazy_sync,
            status: "DELETED",
          } as EazySyncInfo,
        };

        onSelectedTargetChange(updatedTarget);

        if (searchResults && onSearchResultsChange) {
          const updatedResults = searchResults.map((item) => (item.building_id === buildingId ? updatedTarget : item));
          onSearchResultsChange(updatedResults);
        }

        onRefresh?.();
        setIsDeleting(false);
      })
      .catch((error) => {
        setIsDeleting(false);
        const httpError = error as HttpError;
        let errorMsg = "操作が失敗しました";

        if (httpError.message === "timeout" || httpError.statusCode === 504) {
          errorMsg = "サーバーへの接続がタイムアウトしました。しばらくしてから再度お試しください。";
        } else if (httpError.errors) {
          if (typeof httpError.errors === "string") {
            errorMsg = httpError.errors;
          } else if (httpError.errors.msg) {
            errorMsg = String(httpError.errors.msg);
          } else if (httpError.errors.message) {
            errorMsg = String(httpError.errors.message);
          }
        }

        const prefix = "failed to delete device from Eazy: delete device failed:";
        if (errorMsg.startsWith(prefix)) {
          errorMsg = errorMsg.substring(prefix.length).trim();
        } else if (
          errorMsg === "failed to get eazy registery by building id: ent: eazy_registery not found" ||
          errorMsg.includes("Invalid format for parameter building_id: error unmarshaling")
        ) {
          errorMsg = "棟IDの登録状況が更新されました。最新の状態を反映するため、ページを再読み込みしてください。";
        } else {
          errorMsg = "削除に失敗しました";
        }

        setErrorMessage(errorMsg);
      });
  };

  useEffect(() => {
    if (!selectedTarget) {
      setIsDeleting(false);
      setErrorMessage(null);
    }
  }, [selectedTarget]);

  const status = selectedTarget?.eazy_sync?.status;
  const isLoggedIn = useMemo(() => (selectedTarget ? isLoggedInStatus(status) : false), [selectedTarget, status]);
  const isDeleted = useMemo(() => (selectedTarget ? isDeletedStatus(status) : false), [selectedTarget, status]);

  return (
    <Space direction="vertical" size="large" style={{ width: "100%" }}>
      {!selectedTarget && searchResults && searchResults.length > 0 && (
        <div
          className="flex items-center justify-center"
          style={{
            height: "52px",
            backgroundColor: EAZY_COLORS.BACKGROUND_GRAY,
          }}
        >
          <Text
            style={{
              color: EAZY_COLORS.TEXT_SECONDARY,
              fontWeight: 400,
              fontSize: "14px",
              lineHeight: "20px",
              textAlign: "center",
            }}
          >
            詳細は、ここに表示してご確認いただけます
          </Text>
        </div>
      )}

      {selectedTarget && (
        <>
          <div
            style={{
              padding: "16px",
              backgroundColor: "#F9FAFB",
              border: "1px solid #E5E7EB",
              borderRadius: "10px",
            }}
          >
            <div style={{ display: "flex", flexDirection: "column" }}>
              <Text
                style={{
                  color: "#364153",
                  fontWeight: 500,
                  fontSize: "14px",
                  lineHeight: "20px",
                  marginBottom: "16px",
                }}
              >
                選択された対象の情報
              </Text>
              <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
                <div>
                  <Text
                    style={{
                      color: "#6A7282",
                      fontWeight: 500,
                      fontSize: "14px",
                      lineHeight: "20px",
                    }}
                  >
                    管理ID:
                  </Text>
                  <Text
                    style={{
                      color: "#000000D9",
                      fontWeight: 400,
                      fontSize: "14px",
                      lineHeight: "20px",
                      marginLeft: "8px",
                    }}
                  >
                    {selectedTarget.eazy_sync?.management_id || "-"}
                  </Text>
                </div>
                <div>
                  <Text
                    style={{
                      color: "#6A7282",
                      fontWeight: 500,
                      fontSize: "14px",
                      lineHeight: "20px",
                    }}
                  >
                    デジタルキー:
                  </Text>
                  <Text
                    style={{
                      color: "#000000D9",
                      fontWeight: 400,
                      fontSize: "14px",
                      lineHeight: "20px",
                      marginLeft: "8px",
                    }}
                  >
                    {selectedTarget.building_id}
                  </Text>
                </div>
                <div>
                  <Text
                    style={{
                      color: "#6A7282",
                      fontWeight: 500,
                      fontSize: "14px",
                      lineHeight: "20px",
                    }}
                  >
                    住所:
                  </Text>
                  <Text
                    style={{
                      color: "#000000D9",
                      fontWeight: 400,
                      fontSize: "14px",
                      lineHeight: "20px",
                      marginLeft: "8px",
                    }}
                  >
                    {selectedTarget.address?.full_address
                      ? `${selectedTarget.address.full_address} ${selectedTarget.name}`
                      : selectedTarget.name}
                  </Text>
                </div>
                <div>
                  <Text
                    style={{
                      color: "#6A7282",
                      fontWeight: 500,
                      fontSize: "14px",
                      lineHeight: "20px",
                    }}
                  >
                    機種:
                  </Text>
                  <Text
                    style={{
                      color: "#000000D9",
                      fontWeight: 400,
                      fontSize: "14px",
                      lineHeight: "20px",
                      marginLeft: "8px",
                    }}
                  >
                    {convertDeviceContract(selectedTarget.property.device_contracts[0] || "") || "-"}
                  </Text>
                </div>
                <div>
                  <Text
                    style={{
                      color: "#6A7282",
                      fontWeight: 500,
                      fontSize: "14px",
                      lineHeight: "20px",
                    }}
                  >
                    登録状態:
                  </Text>
                  <Text
                    style={{
                      color: "#000000D9",
                      fontWeight: 400,
                      fontSize: "14px",
                      lineHeight: "20px",
                      marginLeft: "8px",
                    }}
                  >
                    {getStatusText(status)}
                  </Text>
                </div>
              </div>
            </div>
          </div>

          {isLoggedIn && (
            <>
              {errorMessage && (
                <div
                  className="flex items-center justify-center"
                  style={{
                    minHeight: "52px",
                    maxHeight: "120px",
                    backgroundColor: EAZY_COLORS.BACKGROUND_GRAY,
                    padding: "12px 16px",
                    overflowY: "auto",
                  }}
                >
                  <Text
                    style={{
                      color: EAZY_COLORS.ERROR,
                      fontWeight: 400,
                      fontSize: "14px",
                      lineHeight: "20px",
                      textAlign: "center",
                      wordBreak: "break-word",
                      whiteSpace: "pre-wrap",
                      maxWidth: "100%",
                    }}
                  >
                    {errorMessage}
                  </Text>
                </div>
              )}
              {isDeleting ? (
                <div
                  className="flex items-center justify-center"
                  style={{
                    height: "52px",
                    backgroundColor: EAZY_COLORS.BACKGROUND_GRAY,
                  }}
                >
                  <Spin size="small" style={{ marginRight: "8px" }} />
                  <Text
                    style={{
                      color: EAZY_COLORS.TEXT_SECONDARY,
                      fontWeight: 400,
                      fontSize: "14px",
                      lineHeight: "20px",
                      textAlign: "center",
                    }}
                  >
                    削除中
                  </Text>
                </div>
              ) : (
                <div style={{ display: "flex", justifyContent: "center" }}>
                  <Button danger icon={<DeleteOutlined />} onClick={handleDeleteClick} type="primary">
                    登録を削除する
                  </Button>
                </div>
              )}
            </>
          )}
        </>
      )}

      {selectedTarget && isDeleted && (
        <>
          <div
            className="flex flex-col"
            style={{
              padding: "12px",
              backgroundColor: "#FFF7E6",
              border: "1px solid #FFE58F",
              borderRadius: "6px",
              gap: "4px",
            }}
          >
            <Text
              style={{
                color: "#D46B08",
                fontWeight: 400,
                fontSize: "14px",
                lineHeight: "20px",
              }}
            >
              当該対象の登録情報は削除済みです。
            </Text>
            <Text
              style={{
                color: "#D46B08",
                fontWeight: 400,
                fontSize: "14px",
                lineHeight: "20px",
              }}
            >
              この棟情報は、自動同期の対象外となります。
            </Text>
          </div>
          <div style={{ display: "flex", justifyContent: "center" }}>
            <Button onClick={() => onSelectedTargetChange(null)} type="primary">
              確定
            </Button>
          </div>
        </>
      )}

      {contextHolder}
    </Space>
  );
};
